import {
  type CompRef,
  type NestedDataPathSegment,
} from '@wix/document-services-types';
import { ManifestComponentExtensionType } from '../stores';
import { ClientSpecMapService } from '../sharedServices';
import { EditorElement, InlineElement } from '@wix/component-protocol/schema';
import type {
  ContainerLayout,
  SimpleContainer,
  ContainerTypesSlot,
  TemplateContainer,
  DataItem,
} from '@wix/ambassador-devcenter-v1-component-type-data/types';
import { DataType } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';
import { SYSTEM_CONTAINER_TYPE } from '../constants/componentTypes';
import { mergeContainerLayouts } from '../middlewares/containerOverrides/utils';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export const BUILDER_COMPONENTS_SLOT_API_MIGRATION_SPEC =
  'specs.editorplatform.BuilderComponentsSlotApiMigration' as const;

type SlotCsmDefinition = {
  data: {
    slotData: {
      exposureAndMaturity?: {
        exposure?: 'PUBLIC';
      };
    };
  };
};

export class ContainerPlatformService extends AbstractComponentsPlatformService {
  private readonly clientSpecMapService: ClientSpecMapService;

  constructor(
    ...args: ConstructorParameters<typeof AbstractComponentsPlatformService>
  ) {
    super(...args);
    this.clientSpecMapService = new ClientSpecMapService(
      this.dm,
      this.editorPointerAPI,
    );
  }

  static getDefinitionByDataPath(
    manifest: EditorElement | InlineElement | null,
    dataPath: string[],
    containerSubType: 'simple',
  ): SimpleContainer | null;
  static getDefinitionByDataPath(
    manifest: EditorElement | InlineElement | null,
    dataPath: string[],
    containerSubType: 'slot',
  ): ContainerTypesSlot | null;
  static getDefinitionByDataPath(
    manifest: EditorElement | InlineElement | null,
    dataPath: string[],
    containerSubType: 'template',
  ): TemplateContainer | null;
  static getDefinitionByDataPath(
    manifest: EditorElement | InlineElement | null,
    dataPath: string[],
    containerSubType: 'simple' | 'slot' | 'template',
  ): SimpleContainer | ContainerTypesSlot | TemplateContainer | null {
    if (
      !manifest?.data ||
      !dataPath ||
      dataPath.length === 0 ||
      !containerSubType
    ) {
      return null;
    }

    try {
      const currentData = manifest.data;
      let currentDataItem: DataItem | undefined;

      for (const pathSegment of dataPath) {
        if (
          currentDataItem?.dataType === 'arrayItems' &&
          currentDataItem?.arrayItems?.data?.items
        ) {
          if (!isNaN(Number(pathSegment))) {
            continue;
          }
          currentDataItem = currentDataItem.arrayItems.data.items[pathSegment];
          continue;
        }

        if (!currentData?.[pathSegment]) {
          return null;
        }

        currentDataItem = currentData[pathSegment] as DataItem;
      }

      if (
        currentDataItem?.dataType !== 'container' ||
        currentDataItem?.container?.containerType !== containerSubType
      ) {
        return null;
      }

      const definition = currentDataItem.container[containerSubType];
      return definition ?? null;
    } catch (error) {
      console.error('Error getting container definition:', error);
      return null;
    }
  }

  static getDataItemsPathBySlotName(
    dataValues: Record<string, unknown>,
    slotKeyName: string,
  ): string[] | null {
    const findPath = (
      obj: any,
      targetId: string,
      currentPath: string[] = [],
    ): string[] | null => {
      if (typeof obj !== 'object' || obj === null) {
        return null;
      }

      for (const [key, value] of Object.entries(obj)) {
        if (value === targetId) {
          return [...currentPath, key];
        }

        if (typeof value === 'object' && value !== null) {
          const result = findPath(value, targetId, [...currentPath, key]);
          if (result) {
            return result;
          }
        }
      }

      return null;
    };

    return findPath(dataValues, slotKeyName);
  }

  static getDataItemsPathFromNestedDataPath(
    dataValues: Record<string, unknown>,
    slotKeyName: string,
    nestedDataPath: NestedDataPathSegment[],
  ): string[] {
    const isArraySegment = (segment: NestedDataPathSegment) =>
      segment.type === 'array' && segment._id;

    const path: string[] = [];
    let current: unknown = dataValues;

    for (const segment of nestedDataPath) {
      path.push(segment.key);

      const next = (current as Record<string, unknown>)?.[segment.key];
      current = next;

      if (isArraySegment(segment) && Array.isArray(next)) {
        const index = next.findIndex(
          (item: Record<string, unknown>) => item?._id === segment._id,
        );
        if (index >= 0) {
          path.push(String(index));
          current = next[index];
        }
      }
    }

    if (slotKeyName) {
      path.push(slotKeyName);
    }

    return path;
  }

  #getCsmSlotDefinition(
    slotId: string,
    slotOwnerPointer: EditorPointer<CompRef>,
  ) {
    const compType = this.clientSpecMapService.getCompType(slotOwnerPointer);

    const appData = this.clientSpecMapService.getAppData(compType);

    const slotComponent = appData?.components?.find(
      (component) => component.componentId === slotId,
    );

    if (!slotComponent) {
      return null;
    }

    return slotComponent as SlotCsmDefinition;
  }

  #getPluginSlotType(csmSlotDefinition: SlotCsmDefinition) {
    const exposure =
      csmSlotDefinition.data.slotData.exposureAndMaturity?.exposure;

    return exposure === 'PUBLIC' ? 'public' : 'private';
  }

  #getPluginSlotDefinition(editorPointer: EditorPointer<CompRef>) {
    const componentType = this.dm.components.getType(
      editorPointer.documentPointer as CompRef,
    );

    if (componentType === SYSTEM_CONTAINER_TYPE) {
      return null;
    }

    const containerOwnerPointer = this.getContainerOwnerPointer(editorPointer);

    if (!containerOwnerPointer) {
      return null;
    }

    const slotManifestDefinition = this.getManifestDefinition(
      editorPointer,
      containerOwnerPointer,
      'slot',
    );

    if (!slotManifestDefinition) {
      return false;
    }

    const { slotId } = slotManifestDefinition ?? {};
    if (!slotId) {
      return false;
    }

    return this.#getCsmSlotDefinition(slotId, containerOwnerPointer);
  }

  getPluginSlots(parentPointer: EditorPointer<CompRef>) {
    return this.dm.components.slots.builder.getWidgetSlots(
      parentPointer.documentPointer,
    );
  }

  getAllDmSlots(parentPointer: EditorPointer<CompRef>) {
    const innerElementPath = parentPointer?.innerElementPath ?? [];

    if (
      !this.experimentsAPI.enabled(BUILDER_COMPONENTS_SLOT_API_MIGRATION_SPEC)
    ) {
      const slotsData: Record<string, CompRef | undefined> =
        innerElementPath?.length > 0
          ? this.dm.components.innerElements.slots.getSlotsData(
              parentPointer.documentPointer,
              innerElementPath,
            )
          : this.dm.components.slots.getSlotsData(
              parentPointer.documentPointer,
            );

      return Object.entries(slotsData).map(([slotName, compRef]) => ({
        slotName,
        compRef,
      }));
    }

    const slotsData =
      innerElementPath.length > 0
        ? this.dm.components.innerElements.slots.getPopulatedComponentContainerInfoMap(
            parentPointer.documentPointer,
            innerElementPath,
          )
        : this.dm.components.slots.builder.getPopulatedComponentContainerInfoMap(
            parentPointer.documentPointer,
          );

    return Object.values(slotsData);
  }

  #getDataPathForSlot(
    dataValues: Record<string, unknown>,
    slot: { slotName: string; nestedDataPath?: NestedDataPathSegment[] },
  ): string[] {
    if (
      this.experimentsAPI.enabled(BUILDER_COMPONENTS_SLOT_API_MIGRATION_SPEC)
    ) {
      const nestedDataPath =
        'nestedDataPath' in slot && Array.isArray(slot.nestedDataPath)
          ? slot.nestedDataPath
          : [];
      return ContainerPlatformService.getDataItemsPathFromNestedDataPath(
        dataValues,
        slot.slotName,
        nestedDataPath,
      );
    }
    return (
      ContainerPlatformService.getDataItemsPathBySlotName(
        dataValues,
        slot.slotName,
      ) ?? [slot.slotName]
    );
  }

  getContainerOwnerPointer(
    editorPointer: EditorPointer<CompRef>,
  ): EditorPointer<CompRef> | null {
    if (
      !this.dm.components.slots.isChildOfSlottedComp(
        editorPointer.documentPointer,
      ) &&
      !/__\d+$/.test(editorPointer.documentPointer.id)
    ) {
      return null;
    }

    const fullPointer =
      this.dm.components.slots.builder.getSlottedComponentOwner(
        editorPointer.documentPointer,
      );

    if (!fullPointer) {
      return null;
    }

    return this.editorPointerAPI.buildEditorPointer(
      fullPointer.compPointer as CompRef,
      fullPointer.innerElementPath,
    );
  }

  getManifestDefinition(
    editorPointer: EditorPointer<CompRef>,
    containerOwnerPointer: EditorPointer<CompRef>,
    containerType: 'simple',
  ): SimpleContainer | null;
  getManifestDefinition(
    editorPointer: EditorPointer<CompRef>,
    containerOwnerPointer: EditorPointer<CompRef>,
    containerType: 'slot',
  ): ContainerTypesSlot | null;
  getManifestDefinition(
    editorPointer: EditorPointer<CompRef>,
    containerOwnerPointer: EditorPointer<CompRef>,
    containerType: 'template',
  ): TemplateContainer | null;
  getManifestDefinition(
    editorPointer: EditorPointer<CompRef>,
    containerOwnerPointer: EditorPointer<CompRef>,
    containerType: 'simple' | 'slot' | 'template',
  ): SimpleContainer | ContainerTypesSlot | TemplateContainer | null {
    const slots =
      containerType === 'slot'
        ? this.getPluginSlots(containerOwnerPointer)
        : this.getAllDmSlots(containerOwnerPointer);

    const compId = editorPointer.documentPointer.id;
    const slot = slots.find(
      ({ compRef }) =>
        compRef?.id === compId ||
        compRef?.id === compId.replace(/(?:__\d+)+$/, ''),
    );

    if (!slot) {
      return null;
    }

    const dataValues = this.virtualElementProvider
      .getManifestQueryAPI()
      .data.getData(
        containerOwnerPointer,
        ManifestComponentExtensionType.BUILDER,
      );

    const dataPath = this.#getDataPathForSlot(dataValues, slot);

    if (!dataPath || dataPath.length === 0) {
      return null;
    }

    const parentEditorElement = this.manifestStore.getResolvedEditorElement(
      containerOwnerPointer,
      {
        useRawManifest: true,
      },
    );

    if (containerType === 'simple') {
      return this.#withPresetContainerLayout(
        ContainerPlatformService.getDefinitionByDataPath(
          parentEditorElement,
          dataPath,
          'simple',
        ),
        containerOwnerPointer,
        parentEditorElement,
        slot.slotName,
      );
    }

    if (containerType === 'template') {
      return this.#withPresetContainerLayout(
        ContainerPlatformService.getDefinitionByDataPath(
          parentEditorElement,
          dataPath,
          'template',
        ),
        containerOwnerPointer,
        parentEditorElement,
        slot.slotName,
      );
    }

    return ContainerPlatformService.getDefinitionByDataPath(
      parentEditorElement,
      dataPath,
      'slot',
    );
  }

  /**
   * Per-preset container layout override: when the owner has an applied
   * preset declaring `presetDefaults.containers.<containerKey>`, its fields
   * override the container's declared layout (unset fields fall back).
   * Keys match by slot name, mirroring the write side
   * (`ManifestQuery.regenerateContainersOnPresetChange`).
   * Inner-element owners are out of scope — an `InlineElement` has no
   * `presets` map, so the overlay no-ops (TODO: [EP-8823]).
   */
  #getPresetContainerLayout(
    containerOwnerPointer: EditorPointer<CompRef>,
    parentEditorElement: EditorElement | InlineElement | null,
    containerKey: string,
  ): ContainerLayout | null {
    if (!this.experimentsAPI.enabled('dmSystemContainerMinWidth')) {
      return null;
    }

    const presets =
      parentEditorElement && 'presets' in parentEditorElement
        ? parentEditorElement.presets
        : undefined;

    if (!presets) {
      return null;
    }

    const appliedPreset = this.virtualElementProvider
      .getManifestQueryAPI()
      .presets.getAppliedPreset(
        containerOwnerPointer,
        ManifestComponentExtensionType.BUILDER,
      );

    if (!appliedPreset) {
      return null;
    }

    return (
      presets[appliedPreset]?.presetDefaults?.containers?.[containerKey] ?? null
    );
  }

  #withPresetContainerLayout<T extends SimpleContainer | TemplateContainer>(
    definition: T | null,
    containerOwnerPointer: EditorPointer<CompRef>,
    parentEditorElement: EditorElement | InlineElement | null,
    containerKey: string,
  ): T | null {
    if (!definition) {
      return null;
    }

    const presetLayout = this.#getPresetContainerLayout(
      containerOwnerPointer,
      parentEditorElement,
      containerKey,
    );

    if (!presetLayout) {
      return definition;
    }

    return {
      ...definition,
      layout: mergeContainerLayouts(definition.layout, presetLayout),
    };
  }

  isMenuItemsContainer(editorPointer: EditorPointer<CompRef>): boolean {
    const containerOwnerPointer = this.getContainerOwnerPointer(editorPointer);
    if (!containerOwnerPointer) {
      return false;
    }

    const slots = this.getAllDmSlots(containerOwnerPointer);
    const slot = slots.find(
      ({ compRef }) => compRef?.id === editorPointer.documentPointer.id,
    );

    if (!slot) {
      return false;
    }

    const parentEditorElement = this.manifestStore.getResolvedEditorElement(
      containerOwnerPointer,
      { useRawManifest: true },
    );

    const innerElementPath = containerOwnerPointer.innerElementPath ?? [];
    const ownerCompData =
      innerElementPath.length > 0
        ? this.dm.components.innerElements.data.get(
            containerOwnerPointer.documentPointer,
            innerElementPath,
          )
        : this.dm.components.data.get(containerOwnerPointer.documentPointer);

    if (!ownerCompData || !parentEditorElement?.data) {
      return false;
    }

    return Object.entries(parentEditorElement.data).some(([key, dataItem]) => {
      if (dataItem?.dataType !== DataType.menuItems) {
        return false;
      }

      const menuId = ownerCompData[key];
      if (typeof menuId !== 'string') {
        return false;
      }

      const cleanMenuId = menuId.startsWith('#') ? menuId.slice(1) : menuId;
      const menuData = this.dm.menu.getById(cleanMenuId);
      if (!menuData) {
        return false;
      }

      return menuData.items.some((item) => item.container === slot.slotName);
    });
  }

  getAutoInjectedEditorPointers(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ): EditorPointer<CompRef>[] {
    const virtualManifest = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers,
      })
      .getVirtualManifest();

    return virtualManifest.manifests
      .filter((x) => x.isAutoInjected)
      .map((x) => x.editorPointer);
  }

  isPrivateSlotPlugin(editorPointer: EditorPointer<CompRef>) {
    const csmSlotDefinition = this.#getPluginSlotDefinition(editorPointer);

    if (!csmSlotDefinition) {
      return false;
    }

    return this.#getPluginSlotType(csmSlotDefinition) === 'private';
  }
}
